/**
 * PB-FOUNDATION builder bootstrap — nav kernel, lifecycle, plugin wiring, cold init, DOM boot.
 * PB-FND-33: extracted from pb-logic-core.liquid.
 * PB-FND-35: kernel-only nav; step DOM in pb-step-dom.js.
 */

import {landingSenderVariant} from '@theme/pb-sender-surprise';
import {applyVariantToSelections} from '@theme/pb-draft-persistence';
import { ensureKeyboardShim, firstFieldOpensKeyboard, hasTouchKeyboard } from '@theme/pb-keyboard-inset';
import { applyStepDelta, jumpToMainStepDom } from '@theme/pb-step-dom';
import { isCartEditMainPresentation, CART_EDIT_FLOW } from '@theme/pb-cart-child-edit';
import { prepareOfferAutofillBeforeRender } from '@theme/pb-entry-offer-autofill';

/**
 * @param {object} deps
 * @param {object} deps.pb — productBuilder instance
 */
export function createBuilderBootstrap(deps) {
  const pb = deps.pb || deps;

  function pbNavKernelDeps() {
    return {
      getBuilderData: function () {
        return window.BUILDER_DATA || {};
      },
      getActiveContext: function () {
        return pb.getActiveContext();
      },
      hasEffectiveOfferBundles: function () {
        return typeof pb.hasEffectiveOfferBundles === 'function' ? pb.hasEffectiveOfferBundles() : false;
      },
      hasAnyAddons: function () {
        return typeof pb.hasAnyAddons === 'function' ? pb.hasAnyAddons() : false;
      },
      hasAddonShell: function (data) {
        return data && data.hasAddonStep === true;
      },
      maybeSkipEmptyOfferStep: function (direction) {
        return typeof pb.maybeSkipEmptyOfferStep === 'function' ? pb.maybeSkipEmptyOfferStep(direction) : false;
      },
      maybeSkipEmptyAddonStep: function (direction) {
        return typeof pb.maybeSkipEmptyAddonStep === 'function' ? pb.maybeSkipEmptyAddonStep(direction) : false;
      },
      maybeSkipDesignLibraryStep: function (direction) {
        return window.PBDesignLibrary && typeof window.PBDesignLibrary.maybeSkipStep === 'function'
          ? window.PBDesignLibrary.maybeSkipStep(direction)
          : false;
      },
      moveStepLegacy: function (delta) {
        applyStepDelta(pb, delta);
      },
      jumpToStepLegacy: function (targetStep) {
        if (window.builderState && window.builderState.mode === 'SUB') {
          const ctx = pb.getActiveContext();
          const target = parseInt(targetStep, 10);
          if (!isNaN(target) && ctx) {
            const delta = target - ctx.step;
            if (delta !== 0) applyStepDelta(pb, delta);
          }
          return;
        }
        jumpToMainStepDom(pb, targetStep);
      },
      getNavContext: function () {
        if (window.PBCartChildEdit && typeof window.PBCartChildEdit.resolveNavContextFromBuilderState === 'function') {
          return window.PBCartChildEdit.resolveNavContextFromBuilderState(
            window.builderState,
            window.BUILDER_DATA,
            window.builderState && window.builderState._pbCartEditCart
          );
        }
        return null;
      },
    };
  }

  function ensureNavKernel() {
    if (!window.PBNavigation) return null;
    if (!pb._pbNavKernel) {
      pb._pbNavKernel = window.PBNavigation.createNavigationKernel(pbNavKernelDeps());
    }
    return pb._pbNavKernel || null;
  }

  function ensureLifecycle() {
    if (!window.PBBuilderLifecycle) return null;
    if (!pb._pbLifecycle) {
      pb._pbLifecycle = window.PBBuilderLifecycle.createBuilderLifecycle({
        onListenerError: function (err) {
          console.warn('[PB lifecycle]', err);
        },
      });
      pb._pbLifecycle.advanceTo('mounted');
      wireLifecycleStepPlugins();
    }
    return pb._pbLifecycle || null;
  }

  function pbOffersPluginDeps() {
    return {
      hasEffectiveOfferBundles: function () {
        return typeof pb.hasEffectiveOfferBundles === 'function' ? pb.hasEffectiveOfferBundles() : false;
      },
      buildOffersHtml: function () {
        if (window.PBOffers && typeof window.PBOffers.buildOffersHtml === 'function') {
          return window.PBOffers.buildOffersHtml();
        }
        return '';
      },
      bindOffersListHandlers: function () {
        if (window.PBOffers && typeof window.PBOffers.bindOffersListHandlers === 'function') {
          window.PBOffers.bindOffersListHandlers();
        }
      },
      checkOfferValidity: function () {
        if (typeof pb.checkOfferValidity === 'function') pb.checkOfferValidity();
      },
      requestSkipEmptyOffer: function (direction) {
        return typeof pb.maybeSkipEmptyOfferStep === 'function' ? pb.maybeSkipEmptyOfferStep(direction) : false;
      },
      applyMarketFilterSync: function () {
        if (window.PBOfferBundleMarketFilter && typeof window.PBOfferBundleMarketFilter.applyToBuilderDataSync === 'function') {
          window.PBOfferBundleMarketFilter.applyToBuilderDataSync(true);
        }
      },
      prepareBeforeRender: function () {
        prepareOfferAutofillBeforeRender(pb);
      },
      getOffersReadinessSignature: function () {
        if (typeof pb.buildOffersReadinessSignature === 'function') {
          return pb.buildOffersReadinessSignature();
        }
        return '';
      },
      cancelOfferSelectionDeferredWork: function () {
        if (typeof pb.cancelOfferSelectionDeferredWork === 'function') pb.cancelOfferSelectionDeferredWork();
      },
      cancelOfferIntroTimersOnly: function () {
        if (typeof pb.cancelOfferIntroTimersOnly === 'function') pb.cancelOfferIntroTimersOnly();
      },
      armOfferIntroPrehide: function (container, introMode) {
        if (typeof pb.armOfferIntroPrehide === 'function') pb.armOfferIntroPrehide(container, introMode);
      },
      maybeRunOfferIntroAfterRender: function (container, introMode) {
        if (typeof pb.maybeRunOfferIntroAfterRender === 'function') pb.maybeRunOfferIntroAfterRender(container, introMode);
      },
      syncBundlePreviewHeroImageFits: function (container) {
        if (typeof pb.syncBundlePreviewHeroImageFits === 'function') pb.syncBundlePreviewHeroImageFits(container);
      },
      shouldSimulateRenderFailure: function () {
        return !!(window.BUILDER_DATA && window.BUILDER_DATA.simulateOffersRenderFailure);
      },
      onRenderError: function (err) {
        console.error('[PB offers plugin]', err);
      },
    };
  }

  function ensureOffersPlugin() {
    if (!window.PBOffers) return null;
    if (!pb._pbOffersPlugin) {
      pb._pbOffersPlugin = window.PBOffers.createOffersPlugin(pbOffersPluginDeps());
    }
    return pb._pbOffersPlugin || null;
  }

  function pbAddonsPluginDeps() {
    return {
      hasAnyAddons: function () {
        return typeof pb.hasAnyAddons === 'function' ? pb.hasAnyAddons() : false;
      },
      buildAddonsHtml: function () {
        if (window.PBAddons && typeof window.PBAddons.buildAddonsHtml === 'function') {
          return window.PBAddons.buildAddonsHtml();
        }
        return '';
      },
      bindAddonsListHandlers: function () {
        if (window.PBAddons && typeof window.PBAddons.bindAddonsListHandlers === 'function') {
          window.PBAddons.bindAddonsListHandlers();
        }
      },
      requestSkipEmptyAddon: function (direction) {
        return typeof pb.maybeSkipEmptyAddonStep === 'function' ? pb.maybeSkipEmptyAddonStep(direction) : false;
      },
      onRenderError: function (err) {
        console.error('[PB addons plugin]', err);
      },
    };
  }

  function ensureAddonsPlugin() {
    if (!window.PBAddons) return null;
    if (!pb._pbAddonsPlugin) {
      pb._pbAddonsPlugin = window.PBAddons.createAddonsPlugin(pbAddonsPluginDeps());
    }
    return pb._pbAddonsPlugin || null;
  }

  function ensureProductGuideStripPlugin() {
    if (!window.PBProductGuideStrip) return null;
    if (!pb._pbGuideStripPlugin) {
      pb._pbGuideStripPlugin = window.PBProductGuideStrip.createProductGuideStripPlugin();
      window.PBProductGuideStrip.installProductGuideStripKeyboardSync(pb);
    }
    return pb._pbGuideStripPlugin || null;
  }

  function wireLifecycleStepPlugins() {
    if (!pb._pbLifecycle) return;
    if (!pb._pbOffersWired) {
      const offersPlugin = ensureOffersPlugin();
      if (offersPlugin && typeof offersPlugin.wire === 'function') {
        offersPlugin.wire(pb._pbLifecycle);
        pb._pbOffersWired = true;
      }
    }
    if (!pb._pbAddonsWired) {
      const addonsPlugin = ensureAddonsPlugin();
      if (addonsPlugin && typeof addonsPlugin.wire === 'function') {
        addonsPlugin.wire(pb._pbLifecycle);
        pb._pbAddonsWired = true;
      }
    }
    if (!pb._pbGuideStripWired) {
      const stripPlugin = ensureProductGuideStripPlugin();
      if (stripPlugin && typeof stripPlugin.wire === 'function') {
        stripPlugin.wire(pb._pbLifecycle);
        pb._pbGuideStripWired = true;
      }
    }
  }

  function resolveLifecycleStepKind(ctx, kernel, s) {
    if (!kernel || !s || !ctx) return null;
    if (s.mode === 'MAIN' && isCartEditMainPresentation(s)) {
      return kernel.getStepKind(ctx.step);
    }
    if (s.mode === 'MAIN') {
      const viewStep = typeof pb.getMainViewportStepNumber === 'function' ? pb.getMainViewportStepNumber() : NaN;
      const step = !isNaN(viewStep) ? viewStep : ctx.step;
      const offerDom = typeof pb.getOfferStepNumberFromDom === 'function' ? pb.getOfferStepNumberFromDom() : NaN;
      const addonDom = typeof pb.getAddonStepNumberFromDom === 'function' ? pb.getAddonStepNumberFromDom() : NaN;
      if (!isNaN(offerDom) && step === offerDom) return 'offer';
      if (!isNaN(addonDom) && step === addonDom) return 'addon';
      return kernel.getStepKind(ctx.step);
    }
    if (s.mode === 'SUB' && s.cartEdit && (s.cartEdit.isAddon || s.cartEdit.isBundleRow)) {
      return kernel.getStepKind(ctx.step);
    }
    return null;
  }

  function emitStepActive() {
    const lifecycle = ensureLifecycle();
    if (!lifecycle) return;
    const s = window.builderState;
    const ctx = pb.getActiveContext();
    const kernel = ensureNavKernel();
    const kind = resolveLifecycleStepKind(ctx, kernel, s);
    wireLifecycleStepPlugins();
    lifecycle.emitStepActive({
      kind: kind,
      context: {
        mode: s ? s.mode : 'MAIN',
        step: ctx ? ctx.step : 1,
        maxSteps: ctx ? ctx.maxSteps : 1,
        isMain: !!(s && s.mode === 'MAIN'),
        flow:
          s?.main?._fieldFlow ||
          (isCartEditMainPresentation(s) ? CART_EDIT_FLOW : 'fresh'),
      },
    });
  }

  function queueStepNavigation(kind, direction) {
    const kernel = ensureNavKernel();
    if (kernel) return kernel.queueStepNavigation(kind, direction);
  }

  function init() {
    if (!window.BUILDER_DATA) return false;
    const DATA = window.BUILDER_DATA;
    const s = window.builderState;
    if (!DATA.mainProduct || !DATA.variants?.length) {
      console.warn('[PB] Missing mainProduct or variants');
      return false;
    }

    if (pb._pbColdInitDone) {
      const marketFilterWarm = window.PBOfferBundleMarketFilter;
      if (marketFilterWarm && typeof marketFilterWarm.applyToBuilderData === 'function') {
        marketFilterWarm.applyToBuilderData(true).catch(function (err) {
          console.warn('[PB] market bundle filter failed', err);
        });
      }
      pb.syncMainMaxStepsFromDom();
      return true;
    }

    pb.validateBuilderData();

    if (DATA.mainProduct.addons) DATA.mainProduct.addons.forEach((a) => (s.addonsDB[a.id] = a));
    if (DATA.bundles)
      DATA.bundles.forEach((b) => b.items.forEach((i) => { if (i.addons) i.addons.forEach((a) => (s.addonsDB[a.id] = a)); }));

    s.main.variants = DATA.variants;
    s.main.options = DATA.options;
    s.main.touched = {};
    s.designLibrary.index = Array.isArray(DATA.designLibraryIndex) ? DATA.designLibraryIndex : [];

    s.main.selections = {};
    // The saved landing draft owns this one explicit entry variant, never a price.
    if(s.mode==='MAIN'&&!s.cartEdit&&!DATA.giftClaim&&!DATA.shopifyBundle&&new URLSearchParams(window.location?.search||'').get('ipLanding')==='1'){
      try{const variant=landingSenderVariant(window.location.search,window.localStorage,s.main.variants);if(variant)applyVariantToSelections(s.main.selections,s.main.options,variant);}catch{/* Blocked storage retains ordinary product entry. */}
    }

    function finishColdInit() {
      ensureLifecycle();
      setTimeout(function () {
        pb.syncMainMaxStepsFromDom();
        if (!s.main.maxSteps || s.main.maxSteps < 1) s.main.maxSteps = 1;
        if (
          window.PBShopifyBundleInherit &&
          typeof window.PBShopifyBundleInherit.applyShopifyBundleOpenStep === 'function'
        ) {
          window.PBShopifyBundleInherit.applyShopifyBundleOpenStep(pb);
        }
        pb.updateAvailability();
        pb.updateImageDisplay(null, null);
        pb.updateUI();
        if (typeof pb.applyButtonWrap === 'function') {
          pb.applyButtonWrap('#main-flow-container');
        }
        if (typeof pb.bindButtonLayoutResizeObserver === 'function') {
          pb.bindButtonLayoutResizeObserver();
        }
        window.productBuilder.ready = true;
        const lifecycle = ensureLifecycle();
        if (lifecycle) lifecycle.advanceTo('inited');
        document.dispatchEvent(new CustomEvent('builder:ready'));
        updateTriggerButtonReady();
      }, 50);
      if (typeof pb.finalizeFieldPresentation === 'function') {
        pb.finalizeFieldPresentation('#main-flow-container');
      }
      emitStepActive();
      pb._pbColdInitDone = true;
    }

    const marketFilter = window.PBOfferBundleMarketFilter;
    const shopifyInherit = window.PBShopifyBundleInherit;
    const shopifyReady =
      DATA.shopifyBundle === true &&
      shopifyInherit &&
      typeof shopifyInherit.ensureShopifyBundleReady === 'function'
        ? shopifyInherit.ensureShopifyBundleReady()
        : Promise.resolve();

    function runColdInitAfterDeps() {
      if (marketFilter && typeof marketFilter.applyToBuilderData === 'function') {
        marketFilter.applyToBuilderData(true).then(finishColdInit).catch(function (err) {
          console.warn('[PB] market bundle filter failed', err);
          finishColdInit();
        });
      } else {
        finishColdInit();
      }
    }

    shopifyReady.then(runColdInitAfterDeps).catch(runColdInitAfterDeps);
    return true;
  }

  return {
    _pbNavKernelDeps: pbNavKernelDeps,
    _ensureNavKernel: ensureNavKernel,
    _ensureLifecycle: ensureLifecycle,
    _pbOffersPluginDeps: pbOffersPluginDeps,
    _ensureOffersPlugin: ensureOffersPlugin,
    _pbAddonsPluginDeps: pbAddonsPluginDeps,
    _ensureAddonsPlugin: ensureAddonsPlugin,
    _wireLifecycleStepPlugins: wireLifecycleStepPlugins,
    _resolveLifecycleStepKind: resolveLifecycleStepKind,
    _emitStepActive: emitStepActive,
    queueStepNavigation,
    init,
  };
}

const BOOTSTRAP_METHODS = [
  '_pbNavKernelDeps',
  '_ensureNavKernel',
  '_ensureLifecycle',
  '_pbOffersPluginDeps',
  '_ensureOffersPlugin',
  '_pbAddonsPluginDeps',
  '_ensureAddonsPlugin',
  '_wireLifecycleStepPlugins',
  '_resolveLifecycleStepKind',
  '_emitStepActive',
  'queueStepNavigation',
  'init',
];

function handleOtherInput(input) {
  if (!input || !input.classList.contains('pb-other-input')) return;
  const pb = window.productBuilder;
  const ctx = pb.getActiveContext();
  const inner = input.closest('.step-content-inner');
  const grid = inner ? inner.querySelector('.btn-grid') : null;
  if (grid) {
    const values = [];
    grid.querySelectorAll('.builder-btn.active').forEach((btn) => {
      const isOther = btn.dataset.other === 'true';
      if (isOther) {
        const v = input.value.trim();
        values.push(v || btn.dataset.value || 'Other');
      } else {
        values.push(btn.dataset.value || '');
      }
    });
    ctx.selections[input.dataset.label] = values.filter(Boolean).join(', ');
    pb.checkButtonValidity(grid);
  } else {
    ctx.selections[input.dataset.label] = input.value.trim();
  }
  pb.syncPersonalizationTemplates(window.builderState.mode === 'MAIN' ? '#main-flow-container' : '#sub-flow-container');
  pb.checkStepValidity();
}

function waitForBootBuilderOpen(timeoutMs) {
  return new Promise(function (resolve, reject) {
    if (typeof window.__pbBootBuilderOpen === 'function') {
      resolve(window.__pbBootBuilderOpen);
      return;
    }
    const deadline = Date.now() + (timeoutMs || 8000);
    function onReady() {
      if (typeof window.__pbBootBuilderOpen === 'function') {
        document.removeEventListener('pb:boot-ready', onReady);
        resolve(window.__pbBootBuilderOpen);
      }
    }
    document.addEventListener('pb:boot-ready', onReady);
    function poll() {
      if (typeof window.__pbBootBuilderOpen === 'function') {
        document.removeEventListener('pb:boot-ready', onReady);
        resolve(window.__pbBootBuilderOpen);
        return;
      }
      if (Date.now() >= deadline) {
        document.removeEventListener('pb:boot-ready', onReady);
        reject(new Error('Customizer failed to load. Please refresh the page and try again.'));
        return;
      }
      setTimeout(poll, 50);
    }
    poll();
  });
}

function updateTriggerButtonReady() {
  const btn = document.getElementById('openBuilderBtn');
  if (!btn || btn.dataset.pbReady === 'true') return;
  // A sold-out product renders its trigger already disabled. Nothing here may hand it back.
  if (btn.dataset.pbSoldOut === '1') return;
  function enableBtn() {
    btn.classList.remove('pb-loading');
    btn.removeAttribute('disabled');
    btn.dataset.pbReady = 'true';
  }
  const data = window.BUILDER_DATA;
  if (data && data.shopifyBundle === true) {
    if (data._pbShopifyBundleReady) {
      enableBtn();
      return;
    }
    const inherit = window.PBShopifyBundleInherit;
    if (inherit && typeof inherit.ensureShopifyBundleReady === 'function') {
      inherit.ensureShopifyBundleReady().then(enableBtn).catch(enableBtn);
      return;
    }
    document.addEventListener('pb:shopify-bundle-ready', enableBtn, { once: true });
    return;
  }
  if (typeof window.__pbBootBuilderOpen === 'function') {
    enableBtn();
    return;
  }
  document.addEventListener('pb:boot-ready', enableBtn, { once: true });
  waitForBootBuilderOpen(8000).then(function () { enableBtn(); }).catch(function () {});
}

function pbAutoResizeIframes(root) {
  (root || document).querySelectorAll('iframe.custom-html-visual').forEach(function (iframe) {
    function resize() {
      try {
        const doc = iframe.contentDocument;
        const h = doc.documentElement.scrollHeight;
        const bodyH = doc.body ? doc.body.scrollHeight : 0;
        const finalH = Math.max(h, bodyH);
        if (finalH > 0) iframe.style.height = finalH + 'px';
      } catch (e) {}
    }
    function resizeWithRetry() {
      resize();
      setTimeout(resize, 100);
      setTimeout(resize, 500);
    }
    if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') resizeWithRetry();
    iframe.addEventListener('load', resizeWithRetry);
  });
}

/**
 * MTFK-2 — hold the keyboard open across the builder's opening, when the first field is one
 * the shopper types into.
 *
 * iOS raises the keyboard only for a focus that happens inside a user gesture. The builder
 * mounts long after the tap ends, so the focus it does on its first field arrives too late
 * and the shopper has to tap the field a second time. Focusing a 1px shim synchronously in
 * the tap keeps the keyboard up; `focusStepInput` then moves focus to the real field, and
 * `completeOpenChrome` blurs the shim if no real field ever appeared.
 *
 * Only when the first field is a text field, so a product that opens on a choice or an
 * image step never flashes a keyboard it has no use for.
 */
function holdKeyboardForFirstField() {
  if (typeof window === 'undefined' || !hasTouchKeyboard(window)) return;
  // The eligible gift choice opens before any product text field.
  if (document.getElementById('pb-surprise-intro')) return;
  if (!window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return;
  const main = window.BUILDER_DATA && window.BUILDER_DATA.mainProduct;
  if (!main || !firstFieldOpensKeyboard(main.config)) return;
  // MKB-2: one builder for the shim, shared with pb-modal-chrome. This file used to carry
  // its own identical copy, and two constructors for an element whose id must match is a
  // drift waiting to happen.
  const shim = ensureKeyboardShim(document.getElementById('product-builder-root'));
  if (!shim) return;
  try {
    shim.focus({ preventScroll: true });
  } catch (e) {
    try {
      shim.focus();
    } catch (e2) {}
  }
}

function wireCoreDomBoot() {
  const btn = document.getElementById('openBuilderBtn');
  let pbOpenGate = null;
  let pbGestureStart = null;
  function builderInputTime(event) {
    const stamp = event?.timeStamp;
    const valid = typeof stamp === 'number' && Number.isFinite(stamp) && stamp > 0;
    return { ms: valid ? stamp : Date.now(), clock: valid ? (stamp > 1e12 ? 'epoch' : 'relative') : 'fallback' };
  }
  function beginBuilderGesture(event) {
    pbGestureStart = builderInputTime(event);
    pbGestureStart.contact = event.type === 'touchstart' || event.pointerType === 'touch' || event.pointerType === 'pen';
  }
  function invokeBuilderOpen(event) {
    if (!btn || btn.disabled) return;
    // Compare when input occurred, not when a busy main thread finally handles it.
    // One tap's pointerup/touchend/click can be processed more than 450ms apart.
    const time = builderInputTime(event);
    const keyboardOrScriptClick = event?.type === 'click' && event.detail === 0;
    const newGesture = pbGestureStart && time.clock === pbGestureStart.clock && time.ms >= pbGestureStart.ms &&
      !(event?.type === 'click' && pbGestureStart.contact);
    const elapsed = pbOpenGate && pbOpenGate.clock === time.clock ? time.ms - pbOpenGate.ms : Infinity;
    // Keep the previous admission while a new gesture starts: its older tail can arrive
    // before or after the new release. Only fallback wall clocks may legitimately go back.
    if (!keyboardOrScriptClick && !newGesture && elapsed < 450 && (time.clock !== 'fallback' || elapsed >= 0)) return;
    // Keyboard and script clicks are independent activations; do not consume a touch tail.
    if (!keyboardOrScriptClick) {
      pbOpenGate = time;
      if (newGesture) pbGestureStart = null;
    }
    // Must be synchronous — this is the only moment iOS will give us a keyboard.
    holdKeyboardForFirstField();
    function openBuilder() {
      waitForBootBuilderOpen(8000).then(function (boot) {
        boot({ entry: 'pdp' }).catch(function (err) {
          alert(err && err.message ? err.message : 'Could not open customizer. Please try again.');
        });
      }).catch(function (err) {
        alert(err && err.message ? err.message : 'Customizer is still loading. Please wait a moment and try again.');
      });
    }
    const data = window.BUILDER_DATA;
    const inherit = window.PBShopifyBundleInherit;
    if (data && data.shopifyBundle === true && inherit && typeof inherit.ensureShopifyBundleReady === 'function') {
      if (!data._pbShopifyBundleReady) {
        btn.classList.add('pb-loading');
        btn.setAttribute('disabled', 'disabled');
      }
      inherit
        .ensureShopifyBundleReady()
        .then(function () {
          btn.classList.remove('pb-loading');
          btn.removeAttribute('disabled');
          openBuilder();
        })
        .catch(function () {
          btn.classList.remove('pb-loading');
          btn.removeAttribute('disabled');
          openBuilder();
        });
      return;
    }
    openBuilder();
  }
  if (btn) {
    // A new physical gesture is intentional, even when it follows the previous tap quickly.
    btn.addEventListener('pointerdown', beginBuilderGesture, { passive: true });
    btn.addEventListener('touchstart', beginBuilderGesture, { passive: true });
    btn.addEventListener('click', invokeBuilderOpen);
    btn.addEventListener(
      'pointerup',
      function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.pointerType === 'touch' || e.pointerType === 'pen') {
          invokeBuilderOpen(e);
        }
      },
      { passive: true }
    );
    btn.addEventListener('touchend', invokeBuilderOpen, { passive: true });
  }
  document.addEventListener('variant:update', function (e) {
    const mainId = window.BUILDER_DATA && window.BUILDER_DATA.mainProduct && window.BUILDER_DATA.mainProduct.id;
    if (!mainId || String((e.detail && e.detail.data && e.detail.data.productId) || '') !== String(mainId)) return;
    const triggerBtn = document.getElementById('openBuilderBtn');
    if (!triggerBtn) return;
    const span = triggerBtn.querySelector('.pb-trigger-text');
    if (!span || !span.dataset.pbBtnTemplate) return;
    const tpl = span.dataset.pbBtnTemplate;
    if (!tpl.includes('{price')) return;
    const res = e.detail && e.detail.resource;
    if (!res || typeof res.price !== 'number') return;
    span.textContent = window.productBuilder.resolveCtaButtonPriceTokens(tpl, res.price);
  });
  const close = document.getElementById('closeBuilderBtn');
  if (close) close.addEventListener('click', () => window.productBuilder.close());
  document.addEventListener('input', function (e) {
    if (e.target.matches && e.target.matches('input.pb-other-input')) handleOtherInput(e.target);
  });
  document.addEventListener('change', function (e) {
    if (e.target.matches && e.target.matches('input.pb-other-input')) handleOtherInput(e.target);
  });
  const runInit = async function () {
    if (typeof window.__pbEnsureFoundationModulesReady === 'function') {
      try {
        await window.__pbEnsureBuilderInitDepsReady?.();
        if (!window.__pbEnsureBuilderInitDepsReady) {
          await window.__pbEnsureFoundationModulesReady();
        }
      } catch (err) {
        console.warn('[PB] foundation modules pre-init failed', err);
      }
    }
    try {
      window.productBuilder.init();
    } catch (e) {
      console.error('[PB] init failed', e);
    }
    updateTriggerButtonReady();
    pbAutoResizeIframes();
  };
  if (btn) {
    runInit();
  }
}

function attachToWindow() {
  if (typeof window === 'undefined') return;
  window.PBBuilderBootstrap = { createBuilderBootstrap };
  window.pbAutoResizeIframes = pbAutoResizeIframes;
}

function install(pb) {
  if (!pb || !window.PBBuilderBootstrap) return false;
  if (pb._pbBootstrapInstalled) return true;
  const bootstrap = createBuilderBootstrap({ pb });
  pb._pbBootstrapInstalled = true;
  pb._pbBootstrap = bootstrap;
  BOOTSTRAP_METHODS.forEach((name) => {
    pb[name] = function (...args) {
      return bootstrap[name](...args);
    };
  });
  return true;
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  return install(window.productBuilder);
}

attachToWindow();

if (typeof window !== 'undefined') {
  window.__pbEnsureBuilderBootstrap = install;
  if (!tryInstall()) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', tryInstall);
    } else {
      tryInstall();
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireCoreDomBoot);
  } else {
    wireCoreDomBoot();
  }
}
